import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  Sparkle, 
  Warning,
  Target,
  ChartBar,
  ArrowRight,
  Info,
  Percent
} from '@phosphor-icons/react';
import { useDashboard } from '../hooks/useDashboard';
import { useHaptic } from '../../../hooks/useHaptic';
import { Button } from '../../../components/ui/Button';
import { SectorConstellationChart } from './SectorConstellationChart';

import { FeatureCorrelationChart } from './FeatureCorrelationChart';
import { DonorComparison } from './DonorComparison';

import { formatIndianNumber } from '../../../utils/currencyFormatter';
import { useScrollRestoration } from '../../../hooks/useScrollRestoration';


function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 15) return 'Good afternoon';
  return 'Good evening';
}

function formatCompactInr(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '₹0';

  const abs = Math.abs(value);
  const formatDecimal = (num: number) => num.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  if (abs >= 10000000) return `₹${formatDecimal(value / 10000000)} Cr`;
  if (abs >= 100000) return `₹${formatDecimal(value / 100000)} Lakh`;
  return `₹${formatIndianNumber(value, '0')}`;
}

function formatPercent(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '0%';
  return `${value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`;
}

/** Small hover-triggered tooltip for explaining a KPI's formula. Dark card variant flips text/border colors. */
function InfoTooltip({ text, dark = false }: { text: string; dark?: boolean }) {
  return (
    <div className="group/tip relative inline-flex">
      <Info
        size={13}
        weight="bold"
        className={dark ? 'text-slate-500 hover:text-slate-300 cursor-help transition-colors' : 'text-slate-300 hover:text-slate-500 cursor-help transition-colors'}
      />
      <div className="pointer-events-none absolute z-30 bottom-full right-0 mb-2 w-56 opacity-0 scale-95 group-hover/tip:opacity-100 group-hover/tip:scale-100 transition-all duration-150 origin-bottom-right">
        <div className="bg-slate-900 text-white text-[10px] font-medium leading-relaxed rounded-lg px-3 py-2 shadow-xl">
          {text}
        </div>
        <div className="absolute top-full right-3 w-2 h-2 bg-slate-900 rotate-45 -mt-1" />
      </div>
    </div>
  );
}

export const DashboardOverview = () => {
  const navigate = useNavigate();
  const { lightTap } = useHaptic();
  const { stats,  loading: dashLoading, error: dashError, isLive, refresh } = useDashboard();
  const userName = localStorage.getItem('name') || 'Admin';

  useScrollRestoration(!dashLoading && !!stats);


  const handleWorkflowClick = (path: string) => {
    lightTap();
    navigate(path);
  };

  if (dashLoading) {
    return (
      <div className="space-y-8 animate-pulse">
        <div className="h-10 w-64 bg-slate-200 rounded-xl" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="space-y-6 lg:space-y-8">
            <div className="h-[240px] bg-slate-200 rounded-3xl" />
            <div className="h-[140px] bg-slate-200 rounded-3xl" />
            <div className="h-[140px] bg-slate-200 rounded-3xl" />
          </div>
          <div className="lg:col-span-2 space-y-6 lg:space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="h-[120px] bg-slate-200 rounded-2xl" />
              <div className="h-[120px] bg-slate-200 rounded-2xl" />
              <div className="h-[120px] bg-slate-200 rounded-2xl" />
              <div className="h-[120px] bg-slate-200 rounded-2xl" />
            </div>
            <div className="h-[300px] bg-slate-200 rounded-3xl" />
          </div>
        </div>
      </div>
    );
  }

  if (dashError) {
    return (
      <div className="bg-white rounded-3xl border border-slate-200/60 shadow-lg p-8 max-w-sm w-full text-center mx-auto mt-16 font-sans">
        <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
          <Warning size={24} className="text-rose-500" />
        </div>
        <p className="font-outfit font-bold text-slate-900 mb-1 text-base">Failed to load overview data</p>
        <p className="text-slate-400 text-xs mb-6 font-medium leading-relaxed">{dashError}</p>
        <Button onClick={refresh} variant="primary" className="mx-auto px-6 py-2.5 rounded-xl font-bold">
          Retry Connection
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8 font-sans pb-12">
      {/* ── Welcome Header ── */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <h1 className="font-outfit font-extrabold text-3xl text-slate-900 tracking-tight leading-none">
          {getGreeting()}, {userName.split(' ')[0]}
        </h1>
        <p className="text-slate-500 text-sm font-medium mt-2">Here's your fundraising platform overview.</p>
      </motion.div>

      {/* ── Top Section: Cards and Constellation ── */}
      <div className="flex flex-col gap-4">
        {/* ── All 6 Cards ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 lg:gap-6">
          {/* 1. Primary KPI Card: Total Alumni Network */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100 flex flex-col justify-center shrink-0"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-slate-50 text-slate-400 rounded-xl flex items-center justify-center">
                <Users size={20} weight="fill" />
              </div>
            </div>
            <div>
              <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider mb-1">Total Alumni Network</p>
              <h2 className="text-4xl font-outfit font-black text-slate-900 tracking-tighter leading-none">
                {formatIndianNumber(stats?.total_alumni, '0')}
              </h2>
            </div>
          </motion.div>

          {/* 2. Untapped Fundraising Potential */}
          <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100 flex flex-col justify-center shrink-0">
            <div className="flex items-center justify-between mb-3">
              <div className="text-slate-400"><Target size={18} weight="fill" /></div>
              <InfoTooltip text="Untapped Fundraising Potential = Σ(Recommended Ask) − Σ(Cumulative Giving) across all alumni." />
            </div>
            <div>
              <div className="text-2xl font-outfit font-black text-slate-900">
                {formatCompactInr(stats?.untapped_fundraising_potential)}
              </div>
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">Untapped Potential</div>
              <div className="text-[9px] font-semibold text-slate-400 mt-1">Estimated unrealized opportunity.</div>
            </div>
          </div>

          {/* 3. Wealth Utilization */}
          <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100 flex flex-col justify-center shrink-0">
            <div className="flex items-center justify-between mb-3">
              <div className="text-slate-400"><Percent size={18} weight="fill" /></div>
              <InfoTooltip text="Wealth Utilization % = [Σ(Recommended Ask) ÷ Σ(Net Worth)] × 100 across all alumni." />
            </div>
            <div>
              <div className="text-2xl font-outfit font-black text-slate-900">
                {formatPercent(stats?.wealth_utilization_percentage)}
              </div>
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">Wealth Utilization</div>
            </div>
          </div>

          {/* 4. Quick Action: Donor Predictor */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            onClick={() => handleWorkflowClick('/donor-predictor')}
            className="relative bg-gradient-to-br from-accent to-orange-600 rounded-3xl p-5 overflow-hidden cursor-pointer group shadow-xl shadow-accent/20 flex flex-col justify-center hover:-translate-y-1 transition-transform duration-300 shrink-0"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-white/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4" />
            <div className="flex justify-between items-start relative z-10 mb-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center text-white backdrop-blur-md shadow-inner">
                <Target size={20} weight="fill" />
              </div>
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                <ArrowRight size={16} className="text-white" />
              </div>
            </div>
            <div className="relative z-10">
              <h3 className="text-white font-outfit font-bold text-lg">Donor Predictor</h3>
              <p className="text-orange-100/80 text-xs font-medium mt-1">Identify high-value targets</p>
            </div>
          </motion.div>

          {/* 5. Quick Action: Proposal Matcher */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            onClick={() => handleWorkflowClick('/proposal-matcher')}
            className="relative bg-slate-900 rounded-3xl p-5 overflow-hidden cursor-pointer group shadow-xl shadow-slate-900/10 flex flex-col justify-center hover:-translate-y-1 transition-transform duration-300 shrink-0"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
            <div className="flex justify-between items-start relative z-10 mb-3">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center text-white backdrop-blur-md">
                <Sparkle size={20} weight="fill" />
              </div>
              <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <ArrowRight size={16} className="text-white" />
              </div>
            </div>
            <div className="relative z-10">
              <h3 className="text-white font-outfit font-bold text-lg">Proposal Matcher</h3>
              <p className="text-slate-400 text-xs font-medium mt-1">AI-driven alumni matching</p>
            </div>
          </motion.div>

          {/* 6. Computed VALS */}
          <div className="bg-slate-900 rounded-3xl p-5 flex flex-col justify-center relative overflow-hidden shrink-0">
            <div className="absolute right-0 bottom-0 w-24 h-24 bg-accent/20 rounded-full blur-2xl translate-x-1/2 translate-y-1/2" />
            <div className="flex justify-between items-start relative z-10 mb-3">
              <div className="text-accent"><ChartBar size={18} weight="fill" /></div>
              {(stats?.vals_min_batch && stats?.vals_max_batch) && (
                <div className="relative flex h-2 w-2" title={isLive ? "System is Live" : "System is Down"}>
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-emerald-400 ${isLive ?  'bg-black/0' : 'bg-red-400'}`}></span>
                  <span className={`relative inline-flex rounded-full h-2 w-2 ${isLive ? 'bg-emerald-500 shadow-[0_0_8px_2px_rgba(16,185,129,0.5)]' : 'bg-red-500 shadow-[0_0_8px_2px_rgba(239,68,68,0.5)]'}`}></span>
                </div>
              )}
            </div>
            <div className="relative z-10">
              <div className="text-2xl font-outfit font-black text-white">{stats?.total_with_vals || 0}</div>
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">Computed VALS</div>
            </div>
          </div>
        </div>

        {/* ── Alumni Constellation Chart (Full Width) ── */}
        <div className="w-full">
          <motion.div 
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="w-full shrink-0 flex flex-col"
            style={{ height: '680px' }}
          >
            <SectorConstellationChart />
          </motion.div>
        </div>
      </div>

      {/* ── Feature Correlations & Donor Comparison ── */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8"
      >
        <div className="lg:col-span-1">
          <FeatureCorrelationChart />
        </div>
        <div className="lg:col-span-2">
          <DonorComparison />
        </div>
      </motion.div>

    </div>
  );
};
